/**
 * # Text
 *
 * A component for displaying text with various styling options.
 *
 * ## Props<C extends React.ElementType>
 *
 * - `bold?: boolean`: Determines if the text should be displayed in bold.
 * - `children?: React.ReactNode`: The content to be displayed within the text component.
 * - `className?: string`: Additional CSS class names to apply to the text component.
 * - `color?: 'black' | 'blue' | 'bright-orange' | 'green' | 'inherit' | 'red' | 'white'`: The color of the text.
 * - `emphasis?: 'high' | 'medium' | 'low' | 'lowest' | 'disabled'`: The emphasis level of the text.
 * - `italic?: boolean`: Determines if the text should be displayed in italic.
 * - `monospace?: boolean`: Determines if the text should be displayed in monospace font.
 * - `paragraph?: boolean`: Determines if the text should be displayed as a paragraph.
 * - `size?: 'xl' | 'l' | 'regular' | 'm' | 's' | 'xs'`: The size of the text.
 * - `withMargin?: boolean`: Determines if the text should have margin.
 * - `id?: string`: The unique identifier of the text component.
 * - `...rest: C`: Any properties of `React.ElementType`
 *
 * ## Usage
 *
 * ```tsx
 * import React from 'react';
 * import Text from './Text';
 *
 * const Example = () => {
 *   return (
 *     <Text bold color="blue" size="xl">
 *       Hello, World!
 *     </Text>
 *   );
 * };
 *
 * export default Example;
 * ```
 */

import classnames from 'classnames';
import React from 'react';
import './text.scss';

export interface Props {
  bold?: boolean;
  children?: React.ReactNode;
  className?: string;
  color?: 'black' | 'blue' | 'bright-orange' | 'green' | 'inherit' | 'red' | 'white';
  emphasis?: 'high' | 'medium' | 'low' | 'lowest' | 'disabled';
  italic?: boolean;
  monospace?: boolean;
  paragraph?: boolean;
  size?: 'xl' | 'l' | 'regular' | 'm' | 's' | 'xs';
  withMargin?: boolean;
  id?: string;
}

export type TextProps<C extends React.ElementType> = PolymorphicComponentProps<C, Props>;

type TextComponent = <C extends React.ElementType = 'span'>(
  props: TextProps<C>
) => React.ReactElement | null;

export const Text: TextComponent = <C extends React.ElementType = 'span'>({
  as,
  bold,
  children,
  className,
  color = 'black',
  emphasis = 'high',
  italic,
  monospace,
  paragraph,
  size = 'regular',
  withMargin,
  ...rest
}: TextProps<C>) => {
  const WrapperElement = as || 'span';
  let hasMargin;
  if (withMargin !== undefined) {
    hasMargin = withMargin;
  } else {
    hasMargin = !!paragraph;
  }
  return (
    <WrapperElement
      className={classnames('a-text', className, {
        [`a-text--color-${color}-${emphasis}`]: color !== 'inherit',
        'a-text--bold': bold,
        'a-text--italic': italic,
        [`a-text--size-${size}`]: size,
        'a-text--monospace': monospace,
        'a-text--paragraph': paragraph,
        'a-text--with-margin': hasMargin
      })}
      {...rest}
    >
      {children}
    </WrapperElement>
  );
};

export default Text;
